import React, { memo, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Modal from '@mui/material/Modal'
import tw from 'twin.macro'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import { ContractorDetails } from 'src/interfaces/contractor'
import { clientSortCollectedData, getCollectDataStepTitle } from 'src/utils/collectedData'
import { CollectedDataStatus } from 'src/constants/collectedData'
import { ContractorPartnerDriverInfo } from './info/ContractorPartnerDriverInfo'
import { ContractorDetailsTabs } from './tabs/ContractorDetailsTabs'
import { OnboardingStepsTab } from './tabs/OnboardingStepsTab'
import { AddOnsTab } from './tabs/AddOnsTab'
import { ActivitiesTab } from './tabs/ActivitiesTab'
import { ContractorTab } from './tabs/enums'
import { ContractorActions } from './contractor-actions/ContractorActions'
import { NotesTab } from './tabs/NotesTab'
import { SubcontractorRequestsActions } from '../subcontractor-request/subcontractor-requests-table/SubcontractorRequestsActions'
import {
  ContractorDetailsModalContext,
  IContractorDetailsModalContext,
} from './ContractorDetailsModal.context'
import { delayMethod } from 'src/utils/baseUtils'
import { FilesTab } from './tabs/FilesTab'

interface Props {
  children?: ReactNode
  contractorDetails: ContractorDetails
  showWaitingForApprovalStepsFirst?: boolean
  flow: 'onboardings' | 'contractors'
  isSubcontractor?: boolean
  isRequestApprove?: boolean
  onClose(): void
  onRefresh(): Promise<void> | void
}

const ContractorDetailsModalComponent = ({
  contractorDetails,
  flow,
  showWaitingForApprovalStepsFirst,
  isSubcontractor,
  isRequestApprove,
  onClose,
  onRefresh,
}: Props) => {
  const delayedRefreshRef = useRef<NodeJS.Timeout | undefined>()
  const container = useMemo(() => document.getElementById('root'), [])

  const [statusIsUpdating, setStatusIsUpdating] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState<ContractorTab>(ContractorTab.Onboarding)

  useEffect(() => {
    return () => {
      if (delayedRefreshRef.current) {
        clearTimeout(delayedRefreshRef.current)

        delayedRefreshRef.current = undefined
      }
    }
  }, [])

  const processedContractorDetails = useMemo(
    () => ({
      ...contractorDetails,
      collectedData: clientSortCollectedData(
        contractorDetails.collectedData,
        showWaitingForApprovalStepsFirst,
      ),
    }),
    [contractorDetails, showWaitingForApprovalStepsFirst],
  )

  const triggerStatusDelayedContractorRefresh = useCallback(() => {
    setStatusIsUpdating(true)

    delayedRefreshRef.current = setTimeout(async () => {
      try {
        await delayMethod(onRefresh)
      } catch {}

      setStatusIsUpdating(false)
    }, 4000)
  }, [onRefresh])

  const ContractorDetailsModalValue = useMemo<IContractorDetailsModalContext>(() => {
    return {
      statusIsUpdating,
      setStatusIsUpdating,
      triggerStatusDelayedContractorRefresh,
    }
  }, [statusIsUpdating, setStatusIsUpdating])

  return (
    <ContractorDetailsModalContext.Provider value={ContractorDetailsModalValue}>
      <Modal
        container={container}
        open
        onClose={onClose}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
        css={tw`flex flex-row w-full h-full`}
      >
        <>
          <div css={tw`w-[900px] h-full bg-white flex flex-col`}>
            <ModalHeader
              title="Contractor details"
              onClose={onClose}
              className={tw`text-black h-[48px]`}
            />
            <div css={tw`flex h-[calc(100vh-48px)]`}>
              <div
                css={tw`max-w-[400px] flex-1 flex flex-col justify-between  border-0 border-r border-solid border-r-[#EDEDED]`}
              >
                <ContractorPartnerDriverInfo
                  flow={flow}
                  isRequestApprove={isRequestApprove}
                  contractorDetails={processedContractorDetails}
                  onboardingStatus={processedContractorDetails.collectedDataStatus}
                  onboardingFailedAt={processedContractorDetails.collectedDataFailedAt}
                  onboardingFailedSteps={processedContractorDetails.collectedData
                    .filter((item) => item.status === CollectedDataStatus.Failed)
                    .map(getCollectDataStepTitle)}
                  onboardingExpiredSteps={
                    processedContractorDetails.deactivationInfo
                      ? [processedContractorDetails.deactivationInfo.stepType]
                      : undefined
                  }
                  isSubcontractor={isSubcontractor}
                  onUpdate={onRefresh}
                />
                {isRequestApprove ? (
                  <SubcontractorRequestsActions
                    subcontractorDetails={processedContractorDetails}
                    onSubmit={onRefresh}
                    onClose={onClose}
                  />
                ) : (
                  <ContractorActions
                    contractorDetails={processedContractorDetails}
                    flow={flow}
                    onRefresh={onRefresh}
                    onClose={onClose}
                    isSubcontractor={isSubcontractor}
                  />
                )}
              </div>
              <div css={tw`w-[500px] flex-1 bg-[#EDEDED]`}>
                {!isRequestApprove && (
                  <ContractorDetailsTabs
                    selectedTab={activeTab}
                    onClick={setActiveTab}
                    isSubcontractor={isSubcontractor}
                  />
                )}
                {activeTab === ContractorTab.Onboarding && (
                  <OnboardingStepsTab
                    contractorDetails={processedContractorDetails}
                    isRequestApprove={isRequestApprove}
                    onRefresh={onRefresh}
                    flow={flow}
                    isSubcontractor={isSubcontractor}
                  />
                )}
                {activeTab === ContractorTab.Addons && (
                  <AddOnsTab
                    contractorDetails={processedContractorDetails}
                    onUpdate={onRefresh}
                    isSubcontractor={isSubcontractor}
                  />
                )}
                {activeTab === ContractorTab.Activities && (
                  <ActivitiesTab contractorId={contractorDetails.id} />
                )}
                {activeTab === ContractorTab.Notes && (
                  <NotesTab contractorId={contractorDetails.id} />
                )}
                {activeTab === ContractorTab.Files && (
                  <FilesTab contractorId={contractorDetails.id} onUpdate={onRefresh} />
                )}
              </div>
            </div>
          </div>
          <div
            id="step-details"
            css={tw`flex flex-1 p-6 max-w-[560px] w-full pointer-events-none`}
          />
        </>
      </Modal>
    </ContractorDetailsModalContext.Provider>
  )
}

export const ContractorDetailsModal = memo(ContractorDetailsModalComponent)
